import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaChartLine, FaInfinity, FaClock, FaCheckCircle, FaCalendarCheck, FaBullseye, FaHandSparkles, FaMugHot, FaFlagCheckered, FaGlobe, FaPalette, FaCircleNotch, FaTrafficLight, FaMapMarkedAlt } from 'react-icons/fa';
import { FaRainbow } from 'react-icons/fa6';
import { SeoManager, getPageSeo, buildFAQSchema } from '../seo';
import TypingSpeedClassifications from '../components/TypingSpeedClassifications';
import FAQSection, { homeFaqs } from '../components/FAQSection';
import '../styles/HomePage.css';


const Hero = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  height: auto;
  min-height: 100vh;
  background-color: var(--background-color-container);
  text-align: center;
  padding: 0 20px;

  @media (max-width: 768px) {
    height: auto; 
    min-height: 100vh; 
    padding: 20px;
    flex-direction: column; 
  }
`;

const HeroContent = styled.div`
  max-width: 800px;

  h1 {
    font-size: var(--h2-font-size-desktop);
    color: var(--hover-border-color);
    margin-bottom: 20px;

    @media (max-width: 768px) {
      font-size: 24px;
    }
  }

  p {
    font-size: 1.2em;
    line-height: 1.6;
    margin-bottom: 30px;
    color: var(--text-color-white);

    a {
      color: var(--hover-border-color);
      text-decoration: underline;
    }

    @media (max-width: 768px) {
      font-size: 1em;
    }
  }

  @media (max-width: 768px) {
    padding-top: 20px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: nowrap; /* Default to single line for desktop/tablet */

  @media (max-width: 768px) {
    flex-wrap: wrap; /* Wrap buttons for mobile */
    max-width: 450px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const TestButton = styled(Link)`
  background-color: var(--hover-border-color);
  color: #121212;
  border: none;
  width: 100px;
  height: 100px;
  font-size: 1.3em;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;

  &:hover {
      transform: scale(1.05);
      background-color: #e6d0ff;
      color: #121212;
  }

  /* Mobile-specific styles */
  @media (max-width: 768px) {
    width: auto; /* Reset width for mobile */
    height: 60px;
    font-size: 1.1rem;
    flex-grow: 1;
    flex-basis: calc(33.33% - 1rem);
    min-width: 80px;
  }
`;

const Section = styled.section`
  padding: 50px 20px;
  background-color: ${props => props.$bg || 'var(--background-color-main)'};
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  text-align: center;
`;

const Feature = styled.div`
  margin-bottom: 30px;
  padding: 2rem;
  background-color: var(--background-color-container);
  border-radius: 8px;
  text-align: center;

  i {
    font-size: 2em;
    color: var(--hover-border-color);
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.5em;
    color: var(--text-color-white);
    margin-bottom: 1rem;

    @media (max-width: 768px) {
      font-size: 1.2em;
    }
  }

  p {
    font-size: 1em;
    color: var(--text-color-white);
    line-height: 1.6;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2.5rem;
  margin-top: 2rem;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const GamesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const GameCardLink = styled(Link)`
  text-decoration: none;
`;

const PlayNowBadge = styled.span`
  display: inline-block;
  margin-top: 1.2rem;
  padding: 0.6em 1.6em;
  border-radius: 8px;
  background: var(--hover-border-color);
  color: #121212;
  font-weight: 700;
  font-size: 1rem;
`;

const typingTests = [
  { label: '15s', path: '/15s-cpm-typing-test/' },
  { label: '30s', path: '/30s-cpm-typing-test/' },
  { label: '1min', path: '/60s-cpm-typing-test/' },
  { label: '3min', path: '/3min-cpm-typing-test/' },
  { label: '5min', path: '/5min-cpm-typing-test/' },
];

const clickTests = [
  { label: '1s', path: '/1s-cps-click-test/' },
  { label: '5s', path: '/5s-cps-click-test/' },
  { label: '15s', path: '/15s-cps-click-test/' },
];

const HomePage = () => {
  const homeSeo = getPageSeo('home');

  return (
    <>
      <SeoManager
        title={homeSeo.title}
        description={homeSeo.description}
        keywords={homeSeo.keywords}
        path={homeSeo.path}
        structuredData={[homeSeo.structuredData, buildFAQSchema(homeFaqs)].filter(Boolean)}
      />
      <main>
        <Hero className="homepage-hero">
          <HeroContent className="homepage-hero-content">
            <h1 className="homepage-section-title">Cheetah Typing Test — Free WPM, CPM & Animal Rank</h1>
            <p>
              Free cheetah typing test. No signup. Type for 15 seconds to 5 minutes, get WPM
              and CPM, and see whether you rank as a Snail, Turtle, Rabbit, Wolf, or Cheetah.
              Looking for clicks per second instead? Take the{' '}
              <Link to="/5s-cps-click-test/">5 second click test</Link>.
            </p>
            <ButtonGroup className="test-buttons">
              {typingTests.map((test) => (
                <TestButton className="test-btn" key={test.path} to={test.path}>
                  {test.label}
                </TestButton>
              ))}
            </ButtonGroup>
            <p style={{ marginTop: '1.5rem', marginBottom: '0.75rem' }}>CPS click tests</p>
            <ButtonGroup className="test-buttons">
              {clickTests.map((test) => (
                <TestButton className="test-btn" key={test.path} to={test.path}>
                  {test.label}
                </TestButton>
              ))}
            </ButtonGroup>
          </HeroContent>
        </Hero>

        <Section className="homepage-features">
          <Container className="container">
            <h2 className="homepage-section-title">Why Choose Us?</h2>
            <FeaturesGrid className="features-grid">
            <Feature className="feature">
                <i><FaChartLine /></i>
              <h3>Easy Analytics</h3>
                <p>Progress with simple analytics. See your strengths and areas for improvement at a glance.</p>
            </Feature>
            <Feature className="feature">
                <i><FaInfinity /></i>
              <h3>Unlimited Practice</h3>
              <p>Improve your typing speed and accuracy with endless practice opportunities.</p>
            </Feature>
            <Feature className="feature">
                <i><FaClock /></i>
              <h3>Flexible Time Limits</h3>
              <p>Choose from a variety of test durations to match your pace and goals.</p>
            </Feature>
            <Feature className="feature">
                <i><FaCheckCircle /></i>
              <h3>Instant Feedback</h3>
              <p>Get instant feedback on your typing. Quickly identify and correct mistakes to improve faster.</p>
            </Feature>
            </FeaturesGrid>
          </Container>
        </Section>

        <Section className="typing-tips-section" $bg="var(--background-color-container)">
          <Container>
            <h2 className="homepage-section-title">How to Improve <span className="h2-white">Your Typing Skills</span></h2>
            <ul>
              <li className="tip-item">
                <div className="tip-icon-wrap"><FaCalendarCheck className="tip-icon" /></div>
                <div>
                  <h3 className="tip-title">Practice Regularly:</h3>
                  <p className="tip-desc">Consistency is key. Set aside at least 15-30 minutes each day for typing practice. Even short, regular sessions are more effective than occasional long ones.</p>
                </div>
              </li>
              <li className="tip-item">
                <div className="tip-icon-wrap"><FaBullseye className="tip-icon" /></div>
                <div>
                  <h3 className="tip-title">Focus on Accuracy:</h3>
                  <p className="tip-desc">Speed will come naturally with practice. Initially, concentrate on typing correctly rather than quickly. Develop muscle memory for accurate keystrokes before pushing for speed.</p>
                </div>
              </li>
              <li className="tip-item">
                <div className="tip-icon-wrap"><FaHandSparkles className="tip-icon" /></div>
                <div>
                  <h3 className="tip-title">Use All Fingers:</h3>
                  <p className="tip-desc">Learn proper finger positioning and touch typing techniques. Each finger should be responsible for specific keys. This approach dramatically increases typing efficiency and speed.</p>
                </div>
              </li>
              <li className="tip-item">
                <div className="tip-icon-wrap"><FaMugHot className="tip-icon" /></div>
                <div>
                  <h3 className="tip-title">Take Breaks:</h3>
                  <p className="tip-desc">Avoid fatigue by taking short breaks during extended typing sessions. Stand up, stretch, and rest your eyes and hands. This prevents strain and helps maintain focus.</p>
                </div>
              </li>
              <li className="tip-item">
                <div className="tip-icon-wrap"><FaFlagCheckered className="tip-icon" /></div>
                <div>
                  <h3 className="tip-title">Set Goals:</h3>
                  <p className="tip-desc">Establish specific, measurable typing goals such as words per minute (WPM) or accuracy percentages. Track your progress and celebrate improvements to stay motivated.</p>
                </div>
              </li>
            </ul>
          </Container>
        </Section>

        <Section className="homepage-games-section" $bg="var(--background-color-container)">
          <Container className="container">
            <h2 className="homepage-section-title">Play a Game</h2>
            <p className="homepage-games-intro">
              Take a break from typing and test your memory, reflexes, and geography skills.
              New to a game? Read the{' '}
              <Link to="/blog/">guides on the blog</Link>.
            </p>
            <GamesGrid className="games-grid">
              <GameCardLink className="game-card-link" to="/higher-or-lower/">
                <Feature className="feature" style={{ cursor: 'pointer', height: '100%' }}>
                  <i><FaGlobe /></i>
                  <h3>Higher or Lower: Countries</h3>
                  <p>
                    Two cards, one question — which number is higher? Compare
                    populations, territory sizes, animal counts, or mix them,
                    score a point for every correct answer, and beat your highscore.
                  </p>
                  <PlayNowBadge className="play-now-badge">Play Now</PlayNowBadge>
                </Feature>
              </GameCardLink>
              <GameCardLink className="game-card-link" to="/color-memory/">
                <Feature className="feature" style={{ cursor: 'pointer', height: '100%' }}>
                  <i><FaPalette /></i>
                  <h3>Color Memory</h3>
                  <p>
                    Memorize a color for 5 seconds, then recreate it with sliders.
                    Score 95 or higher to earn a point and keep going.
                  </p>
                  <PlayNowBadge className="play-now-badge">Play Now</PlayNowBadge>
                </Feature>
              </GameCardLink>
              <GameCardLink className="game-card-link" to="/stop-the-wheel/">
                <Feature className="feature" style={{ cursor: 'pointer', height: '100%' }}>
                  <i><FaCircleNotch /></i>
                  <h3>Stop the Wheel</h3>
                  <p>
                    30 levels: stop the wheel on the target. It starts fast,
                    then shrinks and speeds up each time you hit it. Miss once
                    and you start over.
                  </p>
                  <PlayNowBadge className="play-now-badge">Play Now</PlayNowBadge>
                </Feature>
              </GameCardLink>
              <GameCardLink className="game-card-link" to="/reaction-test/">
                <Feature className="feature" style={{ cursor: 'pointer', height: '100%' }}>
                  <i><FaTrafficLight /></i>
                  <h3>F1 Reaction Test</h3>
                  <p>
                    Five red lights come on one by one. Click the instant they go out —
                    jump early and you fail. How fast is your launch?
                  </p>
                  <PlayNowBadge className="play-now-badge">Play Now</PlayNowBadge>
                </Feature>
              </GameCardLink>
              <GameCardLink className="game-card-link" to="/guess-the-country/">
                <Feature className="feature" style={{ cursor: 'pointer', height: '100%' }}>
                  <i><FaMapMarkedAlt /></i>
                  <h3>Guess the Country</h3>
                  <p>
                    Read the clue, then click the matching country on a world map.
                    Capitals, landmarks, and geography facts — miss once and the run is over.
                  </p>
                  <PlayNowBadge className="play-now-badge">Play Now</PlayNowBadge>
                </Feature>
              </GameCardLink>
              <GameCardLink className="game-card-link" to="/which-country-is-gayer/">
                <Feature className="feature" style={{ cursor: 'pointer', height: '100%' }}>
                  <i><FaRainbow /></i>
                  <h3>Which Country Is Gayer?</h3>
                  <p>
                    Higher or Lower for LGBT-rights indexes. Two countries, one
                    number — pick who is gayer or less gay. Winner stays. Wrong
                    pick ends the run.
                  </p>
                  <PlayNowBadge className="play-now-badge">Play Now</PlayNowBadge>
                </Feature>
              </GameCardLink>
            </GamesGrid>
          </Container>
        </Section>

        <Section className="homepage-speed-levels">
          <Container className="container">
            <TypingSpeedClassifications />
          </Container>
        </Section>
        <FAQSection mode="home" />
        </main>
    </>
  );
}

export default HomePage;